import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TableContainer,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { axiosInstance, useClientInfo, useAppContext } from "../utils/commonImports";
import MyDatePicker from "../components/MyDatePicker";
import { resolveDateRange } from "../utils/fy";

const REPORTS_PATH = "/reports"; // change if your dashboard route differs

/* =========================
   HELPERS
========================= */
const fmtDate = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "");
const n = (v) => Number(v || 0);

const groupBy = (rows, key) =>
  rows.reduce((acc, r) => {
    const k = r[key] || "UNKNOWN";
    if (!acc[k]) acc[k] = [];
    acc[k].push(r);
    return acc;
  }, {});

/* =========================
   CONFIG MAP
========================= */
const viewConfig = {
  TRIPSHEET: { key: null, label: "Tripsheet Wise" },
  CUSTOMER: { key: "customer_name", label: "Customer Wise" },
  VEHICLE: { key: "vehicle_display", label: "Vehicle/Vendor Wise" },
  VG: { key: "assigned_vehicle_group_name", label: "Vehicle Group Wise" },
};

/* =========================
   COMPONENT
========================= */
export default function ComparisonReport() {
  const navigate = useNavigate();

  const { clientId } = useClientInfo();
  const { selectedFinancialYear, financialYearLoading } = useAppContext();
  const fy = selectedFinancialYear?.label || ""; // display label e.g. "2025-26"

  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState("STATEMENT");
  const [view, setView] = useState("TRIPSHEET");
  const [filterValue, setFilterValue] = useState("ALL");
  const [subGroup, setSubGroup] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  const handleBack = () => navigate(REPORTS_PATH);

  // Fetch the selected FY's records. The backend requires the exact `fyid` key.
  const loadReport = useCallback(async () => {
    if (!clientId || financialYearLoading) return;

    const selectedFyId = selectedFinancialYear?.fyid;
    if (!selectedFyId) {
      setRows([]);
      setReportError("A valid financial year ID (fyid) is required.");
      return;
    }

    setLoading(true);
    setReportError("");

    try {
      const { from, to } = resolveDateRange(
        selectedFinancialYear,
        null,
        null,
        true
      );

      const response = await axiosInstance.get("/api/tripsheets/report", {
        params: {
          client_id: clientId,
          fyid: selectedFyId,
          // Keep the alias for compatibility with any existing route code.
          fy_id: selectedFyId,
          from_date: from,
          to_date: to,
          fy,
        },
      });

      const payload = response?.data;
      const records = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.rows)
            ? payload.rows
            : null;

      if (!records) {
        throw new Error(
          payload?.message || "The report API returned an unexpected response."
        );
      }

      setRows(records);
    } catch (error) {
      console.error(
        "Failed to load comparison report:",
        error?.response?.data || error
      );
      setRows([]);
      setReportError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to load comparison report."
      );
    } finally {
      setLoading(false);
    }
  }, [clientId, financialYearLoading, selectedFinancialYear?.fyid, selectedFinancialYear, fy]);

  // Load automatically when client or selected FY changes.
  useEffect(() => {
    if (!clientId || financialYearLoading) return;
    setFromDate(null);
    setToDate(null);
    loadReport();
  }, [clientId, selectedFinancialYear?.fyid, financialYearLoading, loadReport]);

  const handlePrint = () => {
    window.print();
  };

  // Excel-compatible CSV export; no additional package dependency required.
  const handleExcelExport = () => {
    const headers = [
      "Tripsheet",
      "Date",
      "Customer",
      "Sales",
      "Vehicle/Vendor",
      "Purchase",
      "Difference",
      "Percentage",
    ];

    const csvRows = filteredRows.map((r) => {
      const sales = n(r.customer_total);
      const purchase = n(r.vendor_total);
      const diff = sales - purchase;
      const pc = sales ? ((diff / sales) * 100).toFixed(2) : "0.00";
      return [
        r.tripsheet_number,
        fmtDate(r.open_date),
        r.customer_name,
        sales.toFixed(2),
        r.vehicle_display,
        purchase.toFixed(2),
        diff.toFixed(2),
        `${pc}%`,
      ];
    });

    const escapeCsv = (value) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = [headers, ...csvRows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\r\n");
    const blob = new Blob(["\\uFEFF", csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `Comparison_Report_${selectedFinancialYear?.label || "FY"}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const subGroupOptions = {
    CUSTOMER: [
      { value: "VEHICLE", label: "Vehicle/Vendor Wise" },
      { value: "VG", label: "Vehicle Group Wise" },
    ],
    VEHICLE: [
      { value: "CUSTOMER", label: "Customer Wise" },
      { value: "VG", label: "Vehicle Group Wise" },
    ],
    VG: [
      { value: "CUSTOMER", label: "Customer Wise" },
      { value: "VEHICLE", label: "Vehicle/Vendor Wise" },
    ],
  };

  /* =========================
     DATE-FILTERED ROWS (FY + From/To)
     Used for dropdown options AND every table below.
  ========================= */
  const { from: rangeFrom, to: rangeTo } = resolveDateRange(selectedFinancialYear,
    fromDate,
    toDate,
  );

  const dateFilteredRows = rows.filter((r) => {
    if (!rangeFrom && !rangeTo) return true;
    const d = dayjs(r.open_date).format("YYYY-MM-DD");
    if (rangeFrom && d < rangeFrom) return false;
    if (rangeTo && d > rangeTo) return false;
    return true;
  });

  /* =========================
     FILTER OPTIONS
  ========================= */
  const key = viewConfig[view].key;

  const options = !key
    ? []
    : [
        "ALL",
        ...Array.from(
          new Set(dateFilteredRows.map((r) => r[key]).filter(Boolean)),
        ),
      ];

  /* =========================
     FILTERED DATA
  ========================= */
  const filteredRows = dateFilteredRows.filter((r) => {
    if (key && filterValue !== "ALL" && r[key] !== filterValue) return false;
    return true;
  });

  /* =========================
     SUMMARY CALC
  ========================= */
  const calc = (data) => {
    const trips = data.length;
    const sales = data.reduce((t, r) => t + n(r.customer_total), 0);
    const purchase = data.reduce((t, r) => t + n(r.vendor_total), 0);
    const diff = sales - purchase;
    const pc = sales ? ((diff / sales) * 100).toFixed(2) : "0.00";
    return { trips, sales, purchase, diff, pc };
  };

  /* =========================
     STATEMENT TABLE
  ========================= */
  const renderStatementTable = (data) => {
    const total = calc(data);

    return (
      <TableContainer
        sx={{
          mb: 3,
          border: "1px solid #bdbdbd",
          borderRadius: "4px",
        }}
      >
        <Table
          size="small"
          sx={{
            borderCollapse: "collapse",
            "& td, & th": {
              border: "1px solid #d0d0d0",
              fontSize: "0.72rem",
              padding: "4px 6px",
            },
          }}
        >
          {/* ================= HEADER ================= */}
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  backgroundColor: "#f4f6f8",
                  fontWeight: 700,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell>Tripsheet</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>

              {/* SALES */}
              <TableCell align="right" sx={{ borderRight: "3px solid #444" }}>
                Sales
              </TableCell>

              <TableCell>Vehicle/Vendor</TableCell>

              {/* PURCHASE */}
              <TableCell align="right" sx={{ borderRight: "3px solid #444" }}>
                Purchase
              </TableCell>

              <TableCell align="right">Diff</TableCell>
              <TableCell align="right">Pc%</TableCell>
            </TableRow>
          </TableHead>

          {/* ================= BODY ================= */}
          <TableBody>
            {data.map((r, i) => {
              const sales = n(r.customer_total);
              const purchase = n(r.vendor_total);
              const diff = sales - purchase;
              const pc = sales ? ((diff / sales) * 100).toFixed(2) : "0.00";

              return (
                <TableRow
                  key={i}
                  sx={{
                    backgroundColor: i % 2 === 0 ? "#fff" : "#fafafa",
                  }}
                >
                  <TableCell>{r.tripsheet_number}</TableCell>
                  <TableCell>{fmtDate(r.open_date)}</TableCell>
                  <TableCell>{r.customer_name}</TableCell>

                  {/* SALES */}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      borderRight: "3px solid #444",
                    }}
                  >
                    {sales.toFixed(2)}
                  </TableCell>

                  <TableCell>{r.vehicle_display}</TableCell>

                  {/* PURCHASE */}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      borderRight: "3px solid #444",
                    }}
                  >
                    {purchase.toFixed(2)}
                  </TableCell>

                  <TableCell align="right">{diff.toFixed(2)}</TableCell>
                  <TableCell align="right">{pc}%</TableCell>
                </TableRow>
              );
            })}

            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          {/* ================= FOOTER ================= */}
          <TableFooter>
            <TableRow
              sx={{
                background: "#f9fafb",
                borderTop: "4px double #333",
                "& td": {
                  fontWeight: 700,
                  fontSize: "0.75rem",
                },
              }}
            >
              {/* first 3 columns */}
              <TableCell colSpan={3}>TOTAL</TableCell>

              {/* SALES TOTAL */}
              <TableCell align="right" sx={{ borderRight: "3px solid #444" }}>
                {total.sales.toFixed(2)}
              </TableCell>

              {/* Vehicle column empty */}
              <TableCell />

              {/* PURCHASE TOTAL */}
              <TableCell align="right" sx={{ borderRight: "3px solid #444" }}>
                {total.purchase.toFixed(2)}
              </TableCell>

              <TableCell align="right">{total.diff.toFixed(2)}</TableCell>
              <TableCell align="right">{total.pc}%</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    );
  };

  /* =========================
     SUMMARY TABLE
  ========================= */
  const renderSummary = () => {
    if (!key) return null;

    const baseRows = filteredRows;
    const grouped = groupBy(baseRows, key);
    const grand = calc(baseRows);

    return (
      <TableContainer
        sx={{
          border: "1px solid #999",
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        <Table
          size="small"
          sx={{
            "& th, & td": {
              border: "1px solid #bbb",
              fontSize: "0.70rem",
              padding: "4px 6px",
            },
          }}
        >
          {/* HEADER */}
          <TableHead>
            <TableRow
              sx={{
                background: "#f2f2f2",
                "& th": {
                  fontWeight: 700,
                  textAlign: "center",
                },
              }}
            >
              <TableCell>{viewConfig[view].label}</TableCell>

              <TableCell align="right">Trips</TableCell>

              <TableCell align="right" sx={{ borderRight: "3px solid #333" }}>
                Sales
              </TableCell>

              <TableCell align="right" sx={{ borderRight: "3px solid #333" }}>
                Purchase
              </TableCell>

              <TableCell align="right">Diff</TableCell>
              <TableCell align="right">Pc%</TableCell>
            </TableRow>
          </TableHead>

          {/* BODY */}
          <TableBody>
            {Object.entries(grouped).map(([name, data], index) => {
              const t = calc(data);

              // ========= NO SUBGROUP =========
              if (!subGroup) {
                return (
                  <TableRow
                    key={name}
                    sx={{
                      background: index % 2 === 0 ? "#fff" : "#fafafa",
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>

                    <TableCell align="right">{t.trips}</TableCell>

                    <TableCell
                      align="right"
                      sx={{ borderRight: "3px solid #333" }}
                    >
                      {t.sales.toFixed(2)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{ borderRight: "3px solid #333" }}
                    >
                      {t.purchase.toFixed(2)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        color: t.diff < 0 ? "red" : "inherit",
                        fontWeight: 600,
                      }}
                    >
                      {t.diff.toFixed(2)}
                    </TableCell>

                    <TableCell align="right">{t.pc}%</TableCell>
                  </TableRow>
                );
              }

              // ========= WITH SUBGROUP =========
              const subKey = viewConfig[subGroup]?.key;
              const subGrouped = groupBy(data, subKey);

              return (
                <React.Fragment key={name}>
                  {/* GROUP HEADER */}
                  <TableRow sx={{ background: "#eef2f7" }}>
                    <TableCell
                      colSpan={6}
                      sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                    >
                      {name}
                    </TableCell>
                  </TableRow>

                  {/* SUB ROWS */}
                  {Object.entries(subGrouped).map(([subName, subRows], i) => {
                    const st = calc(subRows);

                    return (
                      <TableRow
                        key={subName}
                        sx={{
                          background: i % 2 === 0 ? "#fff" : "#fafafa",
                        }}
                      >
                        <TableCell sx={{ pl: 4 }}>{subName}</TableCell>

                        <TableCell align="right">{st.trips}</TableCell>

                        <TableCell
                          align="right"
                          sx={{ borderRight: "3px solid #333" }}
                        >
                          {st.sales.toFixed(2)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{ borderRight: "3px solid #333" }}
                        >
                          {st.purchase.toFixed(2)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            color: st.diff < 0 ? "red" : "inherit",
                            fontWeight: 600,
                          }}
                        >
                          {st.diff.toFixed(2)}
                        </TableCell>

                        <TableCell align="right">{st.pc}%</TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {baseRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          {/* GRAND TOTAL */}
          <TableFooter>
            <TableRow
              sx={{
                background: "#f9fafb",
                borderTop: "4px double #333",

                "& td": {
                  fontWeight: 700,
                  borderTop: "none",
                },
              }}
            >
              <TableCell>GRAND TOTAL</TableCell>

              <TableCell align="right">{grand.trips}</TableCell>

              <TableCell align="right" sx={{ borderRight: "3px solid #333" }}>
                {grand.sales.toFixed(2)}
              </TableCell>

              <TableCell align="right" sx={{ borderRight: "3px solid #333" }}>
                {grand.purchase.toFixed(2)}
              </TableCell>

              <TableCell align="right">{grand.diff.toFixed(2)}</TableCell>

              <TableCell align="right">{grand.pc}%</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    );
  };

  /* =========================
     MAIN RENDER LOGIC
  ========================= */
  const renderReport = () => {
    if (mode === "SUMMARY") return renderSummary();

    if (filterValue === "ALL" && key) {
      // FIX: group the FILTERED rows (was `rows`, which ignored the date/FY filter)
      const grouped = groupBy(filteredRows, key);

      if (filteredRows.length === 0) return renderStatementTable([]);

      return Object.entries(grouped).map(([name, data]) => (
        <Box key={name}>
          <Typography fontWeight={700} mt={2} mb={1}>
            {name}
          </Typography>
          {renderStatementTable(data)}
        </Box>
      ));
    }

    return renderStatementTable(filteredRows);
  };

  /* =========================
     UI
  ========================= */
  return (
    <Box sx={{ mb: 1 }}>
      <style>{`
        @media print {
          .comparison-no-print { display: none !important; }
          .comparison-report-paper { box-shadow: none !important; border: 0 !important; }
          body { background: #fff !important; }
        }
      `}</style>
      {/* HEADER AND REPORT ACTIONS */}
      <Box
        className="comparison-no-print"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
        flexWrap="wrap"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
        <Tooltip title="Back">
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              variant="body2"
              onClick={() => navigate(REPORTS_PATH)}
            >
              Reports
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              Comparison Statement
            </Typography>
          </Breadcrumbs>

          <Typography variant="h6" fontWeight={700}>
            Comparison Report
            {fy && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                FY {fy}
              </Typography>
            )}
          </Typography>
        </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={loadReport}
            disabled={loading || financialYearLoading || !clientId || !selectedFinancialYear?.fyid}
            sx={{ minWidth: 105, textTransform: "uppercase" }}
          >
            {loading ? "Loading" : "Search"}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={loading}
            sx={{ minWidth: 105, textTransform: "uppercase" }}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={handlePrint}
            disabled={loading}
            sx={{ minWidth: 105, textTransform: "uppercase" }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExcelExport}
            disabled={loading || filteredRows.length === 0}
            sx={{ minWidth: 105, textTransform: "uppercase" }}
          >
            Excel
          </Button>
        </Box>
      </Box>

      {/* FILTER TOOLBAR */}
      <Box
        className="comparison-no-print"
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1.5,
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "visible",
          pb: 0.5,

          background: "#fafafa",
          border: "1px solid #e0e0e0",
          borderRadius: "6px",
          padding: "10px 12px",

          "& .MuiFormControl-root": {
            margin: 0,
          },
        }}
      >
        {/* FROM DATE */}
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          <MyDatePicker
            label="From Date"
            value={fromDate}
            onChange={setFromDate}
            inputStyle={{
              height: 36,
              fontSize: "0.75rem",
              padding: "4px 6px",
            }}
            containerStyle={{
              width: 150,
              marginBottom: 0,
            }}
          />
        </Box>

        {/* TO DATE */}
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          <MyDatePicker
            label="To Date"
            value={toDate}
            onChange={setToDate}
            inputStyle={{
              height: 36,
              fontSize: "0.75rem",
              padding: "4px 6px",
            }}
            containerStyle={{
              width: 150,
              marginBottom: 0,
            }}
          />
        </Box>

        {/* MODE */}
        <FormControl
          size="small"
          sx={{
            minWidth: 180,
            "& .MuiInputBase-root": {
              height: 36,
            },
          }}
        >
          <InputLabel>Mode</InputLabel>
          <Select
            value={mode}
            label="Mode"
            onChange={(e) => setMode(e.target.value)}
          >
            <MenuItem value="STATEMENT">Statement</MenuItem>
            <MenuItem value="SUMMARY">Summary</MenuItem>
          </Select>
        </FormControl>

        {/* VIEW */}
        <FormControl
          size="small"
          sx={{
            minWidth: 220,
            "& .MuiInputBase-root": {
              height: 36,
            },
          }}
        >
          <InputLabel>View</InputLabel>
          <Select
            value={view}
            label="View"
            onChange={(e) => {
              setView(e.target.value);
              setFilterValue("ALL");
              setSubGroup(""); // avoid a stale sub group from the previous view
            }}
          >
            {Object.entries(viewConfig).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* FILTER */}
        {key && (
          <FormControl
            size="small"
            sx={{
              minWidth: 220,
              "& .MuiInputBase-root": {
                height: 36,
              },
            }}
          >
            <InputLabel>Select</InputLabel>
            <Select
              value={options.includes(filterValue) ? filterValue : "ALL"}
              label="Select"
              onChange={(e) => setFilterValue(e.target.value)}
            >
              {options.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* SUB GROUP */}
        {mode === "SUMMARY" && subGroupOptions[view] && (
          <FormControl
            size="small"
            sx={{
              minWidth: 220,
              "& .MuiInputBase-root": {
                height: 36,
              },
            }}
          >
            <InputLabel>Sub Group</InputLabel>
            <Select
              value={subGroup}
              label="Sub Group"
              onChange={(e) => setSubGroup(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {subGroupOptions[view].map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {reportError && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {reportError}
        </Alert>
      )}
      {loading && (
        <Box display="flex" alignItems="center" gap={1} py={1}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading comparison report...
          </Typography>
        </Box>
      )}

      {/* SELECTED FILTER TITLE */}
      {filterValue !== "ALL" && key && (
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.95rem",
            mt: 1,
            mb: 1,
            color: "#222",
          }}
        >
          {filterValue}
        </Typography>
      )}

      {/* REPORT */}
      <Paper className="comparison-report-paper" sx={{ p: 2 }}>{renderReport()}</Paper>
    </Box>
  );
}
